// 正文标题的锚点。`Markdown.tsx`（渲染时给 h2/h3 挂 id）与 `Wiki/Toc.tsx`
// （生成目录链接）必须算出**同一个** id，否则点目录跳不到任何地方 —— 而那种
// 坏法没有任何测试或类型检查拦得住，只能靠点一下发现。所以 id 的算法在这里，
// 只有一处。

// 为什么不装 rehype-slug：它按英文规则 slugify，纯中文标题会被压成空串，
// 于是整份目录的锚点全撞在同一个 `#`。
//
// 改用**源码行号**当 id。这个选择的关键性质是两边都不需要计数器：
// - 目录那侧逐行扫 body_md，行号是现成的
// - 渲染那侧从 react-markdown 传进来的 mdast 节点上读 node.position.start.line
//
// 计数器（「第 N 个 h2」）看起来更自然，但它会漂：setext 形式的标题
// （`标题` 换行 `---`）被 remark 认成 h2，而下面那个正则认不出来 —— 于是两边
// 的计数从那一行起全部错开，目录里每一条都跳到**别的**小节。行号方案在同一
// 情况下只是「那一条不出现在目录里」，锚点本身仍然正确。
export const headingId = (line: number) => `wiki-h-${line}`;

export type WikiHeading = {
  id: string;
  text: string;
  // 只有这两级：h1 是页面标题的位置（正文里一般不用），h4 以下进目录会变成一团。
  level: 2 | 3;
};

// 从 markdown 源码里抓 h2 / h3。§7.3：不入库 —— 它是正文的纯函数，存下来就要
// 维护它和正文的一致性。
export const extractHeadings = (bodyMd: string | null): WikiHeading[] => {
  if (!bodyMd) return [];

  const headings: WikiHeading[] = [];
  // 围栏内的 `## xxx` 是代码，不是标题。不跳过的话「一段讲 markdown 的正文」
  // 会把自己的示例标题灌进目录。
  let inFence = false;

  bodyMd.split('\n').forEach((raw, index) => {
    // ``` 与 ~~~ 都认；只看行首（缩进 3 空格以内仍是围栏，再多就是缩进代码块，
    // 而缩进代码块里的 # 本来也不会被认成标题）。
    if (/^\s{0,3}(```|~~~)/.test(raw)) {
      inFence = !inFence;
      return;
    }
    if (inFence) return;

    const matched = raw.match(/^(#{2,3})\s+(.+?)\s*#*\s*$/);
    if (!matched) return;

    headings.push({
      // +1：数组下标从 0 起，而 mdast 的 position.start.line 从 1 起。
      id: headingId(index + 1),
      // 标题里的行内标记（`code`、**粗**、[链接]()）在目录里剥掉：目录一行只有
      // 190px，留着标记符号比留着强调更占地方。
      text: matched[2]
        .replace(/`([^`]+)`/g, '$1')
        .replace(/\*\*([^*]+)\*\*/g, '$1')
        .replace(/\*([^*]+)\*/g, '$1')
        .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
        .trim(),
      level: matched[1].length === 2 ? 2 : 3,
    });
  });

  return headings;
};
